import { Header } from '../../Header';
import { Footer } from '../../Footer';
import { HomeHero } from '../../blog/HomeHero';
import { PostCard } from '../../blog/PostCard';
import { NewsletterSignupForm } from '../../blog/NewsletterSignupForm';
import { PageType, blogCategoryPath } from '../../Router';
import Link from 'next/link';
import type { BlogPostDocument, BlogCategoryDocument } from '../../../firebase/blog-db';
import { FileQuestion } from 'lucide-react';

export interface CategorySection {
  category: BlogCategoryDocument;
  posts: BlogPostDocument[];
}

interface BlogHomePageProps {
  onNavigate: (page: PageType) => void;
  featured: BlogPostDocument | null;
  sections: CategorySection[];
}

export function BlogHomePage({ onNavigate, featured, sections }: BlogHomePageProps) {
  return (
    <div className="min-h-screen bg-white">
      <Header onNavigate={onNavigate} />
      <main className="pt-14">
        {/* The masthead + hero image are the site's permanent visual identity —
            always rendered, regardless of whether any posts exist yet, rather
            than being replaced by a skeleton/empty message. */}
        <HomeHero featured={featured} />

        {!featured && sections.length === 0 ? (
          <div className="text-center py-24">
            <FileQuestion className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No articles published yet. Check back soon.</p>
          </div>
        ) : (
          <>
            {sections.map((section, i) => (
              <section key={section.category.id} className={i % 2 === 1 ? 'bg-gray-50' : 'bg-white'}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
                  <Link href={blogCategoryPath(section.category.slug)} className="inline-block mb-8 group">
                    <h2 className="font-blocky text-2xl sm:text-3xl text-hustlance-primary group-hover:text-hustlance-secondary transition-colors">
                      {section.category.name}
                    </h2>
                  </Link>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
                    {section.posts.map(post => <PostCard key={post.id} post={post} variant="grouped" />)}
                  </div>
                </div>
              </section>
            ))}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
              <NewsletterSignupForm />
            </div>
          </>
        )}
      </main>
      <Footer onNavigate={onNavigate} />
    </div>
  );
}
